import React, { type ReactNode, useCallback, useRef, useState } from 'react';
import {
  TextField as TextFieldRAC,
  Input as InputRAC,
  Group as GroupRAC,
} from 'react-aria-components';

import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';
import {
  formatPrice,
  getCurrencyAriaLabel,
} from '../shared/payment-field-utils';

import { type PaymentInputFieldProps } from './payment-input-field-props';
import { toProductValue } from './payment-input-field-utils';

export type PaymentInputProps = {
  children: ReactNode;
};

const PaymentInput = ({ children }: PaymentInputProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
    validation,
  } = useFieldPropsV2<PaymentInputFieldProps>();

  const { regionalFormat } = useConfig();
  const paymentValidation =
    validation?.predefined?.paymentOptions?.products?.[0];

  const [paymentValue, setPaymentValue] = useState(
    String(value?.at(0)?.price ?? ''),
  );

  const handleOnBlur = useCallback(() => {
    const price = formatPrice(paymentValue, regionalFormat);
    setPaymentValue(price ? price : '');
    onBlur?.();
  }, [paymentValue, regionalFormat, onBlur]);

  const handleOnChange = useCallback(
    (newValue: string) => {
      setPaymentValue(newValue);
      const filteredInput = formatPrice(newValue, regionalFormat);
      if (filteredInput) {
        const normalizedValue = Number(filteredInput.replace(/,/g, '.'));
        onChange([toProductValue(paymentValidation!, normalizedValue)]);
      } else {
        onChange(null);
      }
    },
    [onChange, paymentValidation, regionalFormat],
  );

  const labelElement = findChildOfType(children, PaymentInput.Label);
  const inputElement = findChildOfType(children, PaymentInput.Input);
  const descriptionElement = findChildOfType(
    children,
    PaymentInput.Description,
  );
  const errorElement = findChildOfType(children, PaymentInput.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <TextFieldRAC
          value={value ? paymentValue : ''}
          onChange={handleOnChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={handleOnBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

export type InputProps = {
  children: ReactNode;
  className?: string;
};

const Input = ({ children, className }: InputProps) => {
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const currencyElement = findChildOfType(children, Input.Currency);
  const valueElement = findChildOfType(children, Input.Value);

  return (
    <GroupRAC
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
    >
      {currencyElement}
      {valueElement}
    </GroupRAC>
  );
};

export type CurrencyProps = {
  className?: string;
};

const Currency = ({ className }: CurrencyProps) => {
  const { currency } = useConfig();
  const { t } = useTranslation();
  const { prefixId } = useFieldAttributes();

  if (!currency) {
    return null;
  }

  return (
    <span
      id={prefixId}
      aria-hidden={false}
      aria-label={getCurrencyAriaLabel(t, currency)}
      className={className}
    >
      {currency.sign || currency.code}
    </span>
  );
};

export type ValueProps = {
  className?: string;
};

const Value = ({ className }: ValueProps) => {
  const { id, placeholder, label } = useFieldPropsV2<PaymentInputFieldProps>();
  const { labelId, prefixId } = useFieldAttributes();

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      className={className}
      placeholder={placeholder}
      aria-label={label}
      aria-labelledby={`${labelId} ${prefixId}`}
      autoComplete="off"
    />
  );
};

Input.Currency = Currency;
Input.Value = Value;

PaymentInput.Label = Label;
PaymentInput.Input = Input;
PaymentInput.Description = Description;
PaymentInput.Error = Error;

export { PaymentInput };
